import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const SUGGESTIONS = [
  "Analisis pola pengeluaranku bulan ini dan berikan saran hemat konkret.",
  "Bagaimana progres tujuan tabungan saya? Berapa lama lagi mencapainya?",
  "Kategori mana yang paling boros dan strategi menguranginya?",
  "Berdasarkan cash flow saya, berapa yang aman untuk diinvestasikan bulan ini?",
];

export default function AIInsight() {
  const { data: history } = useSWR("/ai/history", fetcher);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setLoading(true);
    try {
      await api.post("/ai/insight", { question: text });
      setQuestion("");
      mutate("/ai/history");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI sedang sibuk, coba lagi");
    } finally { setLoading(false); }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-center gap-3">
        <Sparkles className="h-5 w-5 text-ai" />
        <p className="label-caps">Intelligence</p>
      </div>
      <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">AI Insight</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
        Tanya AI tentang keuanganmu. AI menganalisis transaksi, akun, dan tujuanmu untuk memberi rekomendasi konkret.
      </p>

      <div className="mt-8 border border-border bg-card p-5 relative overflow-hidden grain-overlay">
        <p className="label-caps mb-3">Pertanyaanmu</p>
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Contoh: Bagaimana caraku menghemat Rp 1jt per bulan?"
          className="min-h-[100px] resize-none bg-background"
          data-testid="ai-question-input"
        />
        <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                onClick={() => ask(s)}
                disabled={loading}
                className="text-xs border border-border bg-background px-3 py-1.5 hover:bg-accent transition-colors disabled:opacity-50"
                data-testid={`ai-suggestion-${i}`}
              >
                {s.slice(0, 40)}…
              </button>
            ))}
          </div>
          <Button
            onClick={() => ask()}
            disabled={loading || !question.trim()}
            className="bg-ai text-background hover:bg-ai/90 h-11 whitespace-nowrap"
            data-testid="ai-submit-btn"
          >
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Menganalisis…</> : <><Sparkles className="h-4 w-4 mr-2" /> Tanya AI</>}
          </Button>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {(!history || history.length === 0) ? (
          <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Belum ada percakapan. Mulai dengan pertanyaan di atas.
          </div>
        ) : history.map((h) => (
          <div key={h.id} className="border border-border bg-card p-5" data-testid={`ai-history-${h.id}`}>
            <div className="flex items-start gap-3 pb-3 border-b border-border">
              <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold flex-shrink-0">U</div>
              <p className="text-sm font-medium">{h.question}</p>
            </div>
            <div className="flex items-start gap-3 pt-3">
              <div className="h-6 w-6 rounded-full bg-ai text-background flex items-center justify-center flex-shrink-0"><Sparkles className="h-3 w-3" /></div>
              <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{h.answer}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
